// ------ Dependencies ------ //
import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'

// ------ CSS ------ //
import styles from './ExtraBadgeTemplateFieldOrdering.module.css'

// ------ Components ------ //
import BackSaveContinueButton from '../../../components/Miscellaneous/BackSaveContinueButton'
import SortableFieldList from '../../../components/Miscellaneous/SortableFieldList/SortableFieldList'

// ------ Services ------ //
import * as badgeService from '../../../services/badgeManagementService'
import * as clientEventService from '../../../services/clientEventService'

const ExtraBadgeTemplateFieldOrdering = ({ user }) => {

    // ------ State ------ //

    // Client-specific
    const [continueButtonVisibility, setContinueButtonVisibility] = useState(true)
    // Extra badges & template settings
    const [extraBadgeFields, setExtraBadgeFields] = useState([])
    const [extraBadgesOptions, setExtraBadgesOptions] = useState([])
    const [selectedExtraBadge, setSelectedExtraBadge] = useState({})
    const [EB, setEB] = useState([])
    // Latest working order lifted from SortableFieldList (for Save / Continue).
    const [workingOrder, setWorkingOrder] = useState([])
    const [message, setMessage] = useState([''])
    const navigate = useNavigate()
    const location = useLocation()
    const passedState = location.state
    const [clientEventInfo, setClientEventInfo] = useState({})
    const clientEventInfoRef = useRef(null);
    if (clientEventInfoRef === null || clientEventInfoRef.current === null) {
        if (!passedState || !passedState.clientName) {
            clientEventInfoRef.current = clientEventService.getClientEvent();
        }
        else {
            let passedData = {
                clientName: passedState.clientName,
                clientId: passedState.clientId,
                eventName: passedState.eventName,
                eventId: passedState.eventId,
            }
            clientEventInfoRef.current = passedData
        }

    }

    useEffect(() => {
        // reflects client/event selection changes made in Header.jsx without polling
        const handleClientEventChange = (e) => {
            // Update clientevent with every render when its value has changed.
            let savedClientEvent = clientEventService.getClientEvent()
            if (clientEventInfoRef.current && clientEventInfoRef.current.eventId !== undefined &&
                savedClientEvent && savedClientEvent.eventId !== undefined &&
                savedClientEvent.eventId !== clientEventInfoRef.current.eventId) {
                setClientEventInfo(savedClientEvent)
                clientEventInfoRef.current = savedClientEvent;
            }
        }
        window.addEventListener('clientEventChange', handleClientEventChange)
        return () => window.removeEventListener('clientEventChange', handleClientEventChange)
    }, [])
    // ------ REST ------ //
    const createRequestBody = async () => {

        let requestBody = {}
        if (clientEventInfo.clientId && clientEventInfo.eventId && selectedExtraBadge) {
            requestBody =
            {
                "clientId": `${clientEventInfo.clientId}`,
                "eventId": `${clientEventInfo.eventId}`,
                "cSSFieldName": `${selectedExtraBadge.extraBadgeNameLabel}`
            }
        }
        

        return await requestBody;
    }

    const fetchExtraBadgesOrderingData = async () => {
        setExtraBadgeFields([])
        const getReqBody = await createRequestBody()
        const data = await badgeService.getExtraBadgeFieldsOrdering(getReqBody)
        setEB(data)
        setExtraBadgeFields(data.extraBadgeOrderingFields)
    }

    const handleSelectChange = e => {
        let foundBadge = extraBadgesOptions.find((item) => item.extraBadgeNameLabel === e.target.value);
        setSelectedExtraBadge(foundBadge)
        // clear out settings and rebuild component
    }

    const createSaveRequest = (items) => {
        const fieldsSave = {
            clientId: clientEventInfo.clientId,
            eventId: clientEventInfo.eventId,
            CSSExtraBadgesField: EB.cssExtraBadgesField,
            badgeName: (EB.badgeName ? EB.badgeName : EB.cssExtraBadgesFieldName),
            CSSExtraBadgesFieldName: EB.cssExtraBadgesFieldName,
            orderingFields: items
        }

        return fieldsSave
    }

    // Persist the current ordering. Uses the working order lifted from the list,
    // falling back to the last-fetched fields if nothing has been reordered yet.
    const persistOrder = async () => {
        const items = workingOrder.length ? workingOrder : extraBadgeFields
        const saveRequest = createSaveRequest(items)
        try {
            await badgeService.saveExtraBadgesFieldsOrdering(saveRequest)
        } catch (err) {
            setMessage(err.message)
        }
    }

    const handleSave = async () => {
        await persistOrder()
        await fetchExtraBadgesOrderingData()
    }

    const handleSaveAndNavigate = async () => {
        await persistOrder()
        navigate(`/ExtraBadgeTemplateStyling`, { state: { clientEventInfo } })
    }

    // ------ useEffects ------ //
    useEffect(() => {
        if (clientEventInfo && clientEventInfo.clientId !== undefined && selectedExtraBadge) {
            fetchExtraBadgesOrderingData()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedExtraBadge])

    useEffect(() => {
        if (clientEventInfo && clientEventInfo.clientId !== undefined && clientEventInfo.clientName) {
            const fetchData = async () => {
                // Conditional to check for saved template vs. new template
                // Probably check if location.state exists from previous page from getView()

                try {
                    const templates = await badgeService.getExtraBadgesTemplates({ clientId: clientEventInfo.clientId, eventId: clientEventInfo.eventId })
                    setExtraBadgesOptions(templates)
                    setSelectedExtraBadge(templates[0])
                }
                catch (error) {
                    console.error('Error fetching ExtraBadgesTemplates JSON: ', error)
                }
            }
            fetchData()
        }

    }, [clientEventInfo])
    useEffect(() => {
        // store into local object
        if (!passedState || !passedState.clientName) {
            //let localclientevent = clientEventService.getClientEvent()
            if (clientEventInfoRef.current) {
                setClientEventInfo(clientEventInfoRef.current)
            }
        }
        else {
            let passedData = {
                clientName: passedState.clientName,
                clientId: passedState.clientId,
                eventName: passedState.eventName,
                eventId: passedState.eventId,
            }
            setClientEventInfo(passedData)
            clientEventInfoRef.current = passedData;
        }

    }, [])

    return (
        <div className="page-area">
            <div className={styles.templateIndex}>
                <h1>Extra Badge Template Field Layout Ordering</h1>
                <BackSaveContinueButton
                    continueButtonVisibility={continueButtonVisibility}
                    handleSaveAndNavigate={handleSaveAndNavigate}
                    handleSave={handleSave}
                    styles={styles}
                    navigate={navigate}
                />

                <br />
                {
                    extraBadgesOptions && extraBadgesOptions.length > 0 ?
                        <>
                            <div>Select extra badge template:
                                <select
                                    name="selectedExtraBadge"
                                    value={selectedExtraBadge.extraBadgeNameLabel}
                                    onChange={handleSelectChange}
                                >
                                    <option disabled={true} value="" selected="selected">
                                        -- Select an option --
                                    </option>
                                    {
                                        extraBadgesOptions.map((ci, idx) => (
                                            <option key={idx}>{ci.extraBadgeNameLabel}</option>
                                        ))
                                    }
                                </select>
                            </div><br />

                            <p className={styles.helpText}>
                                Reorder the fields, then click Save (or Save &amp; Continue).
                                Drag the handle, use the up/down arrows, or type a rank to move a field.
                            </p>

                            <div className={styles.OrderingContainer}>
                                <SortableFieldList
                                    items={extraBadgeFields}
                                    getId={(item) => item.id}
                                    getLabel={(item) => item.cssFieldName}
                                    onChange={setWorkingOrder}
                                    showSaveButton={false}
                                    emptyText="No fields found for this template."
                                />
                            </div>
                        </>
                        :
                        <>
                            <div>No extra badges setup!</div>
                            <Link to="/BadgeToERSPropertyMapping"
                                className={styles.eventLinks}
                                state={clientEventInfo}
                            >
                                <h3>Go to Mapping Page</h3>
                            </Link>
                        </>
                }

                <br /> <br />
                <BackSaveContinueButton
                    continueButtonVisibility={continueButtonVisibility}
                    handleSaveAndNavigate={handleSaveAndNavigate}
                    handleSave={handleSave}
                    styles={styles}
                    navigate={navigate}
                />

                <br />
            </div>
        </div>
    );
}

export default ExtraBadgeTemplateFieldOrdering;
